import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Network, ArrowLeft, Bot, Trash2, Plus, ArrowRight, Sparkles, Eye, Package } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Tabs,
  TabsList,
  TabTrigger,
  TabContent,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  StatusBadge,
  IconButton,
  type StatusType,
} from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { stripImportedField } from '@/lib/utils';
import {
  useHive,
  useUpdateHive,
  useDeleteHive,
  useAddAgentToHive,
  useRemoveAgentFromHive,
  useHiveGraph,
} from '@/features/agents/hives/hooks/useHives';
import { useHandoffs, useDeleteHandoff } from '@/features/agents/hives/hooks/useHandoffs';
import { useAgents } from '@/features/agents/hooks/useAgents';
import { HiveCapabilitiesTab } from '@/features/capabilities/HiveCapabilitiesTab';
import { PipelinePolicyOverrides } from '@/features/llm-config/components/PipelinePolicySections';
import { HiveGraphView } from '@/features/agents/hives/components/HiveGraphView';
import type { Hive, HiveUpdate, Handoff, GraphNode, GraphEdge } from '@/features/agents/hives/types';
import {
  useHiveSkillBindings,
  useDeleteHiveSkillBinding,
} from '@/features/skills';
import { HiveSkillAddModal } from './modals/HiveSkillAddModal';
import { HiveSkillInheritanceModal } from './modals/HiveSkillInheritanceModal';

// =============================================================================
// Hive Detail Page
// =============================================================================

export function HiveDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: hive, isLoading, error } = useHive(id);
  const { data: graphData } = useHiveGraph(id);
  const { data: handoffsData } = useHandoffs(id);
  const { data: allAgents = [] } = useAgents();

  // ISS-409/412: entidades `use_only` son cajas negras de plataforma — los
  // campos de definición no se editan y no se exportan a CAC.
  const isUseOnly = hive?.delivery_mode === 'use_only';

  const updateMutation = useUpdateHive(id ?? '');
  const deleteMutation = useDeleteHive();
  const addAgentMutation = useAddAgentToHive(id ?? '');
  const removeAgentMutation = useRemoveAgentFromHive(id ?? '');
  const deleteHandoffMutation = useDeleteHandoff(id ?? '');

  const handoffs: Handoff[] = useMemo(() => {
    if (!handoffsData) return [];
    return Array.isArray(handoffsData) ? handoffsData : (handoffsData as { data?: Handoff[] }).data ?? [];
  }, [handoffsData]);

  const graph = graphData as { nodes?: GraphNode[]; edges?: GraphEdge[] } | undefined;

  // Agent name lookup
  const agentMap = useMemo(() => {
    const agents = Array.isArray(allAgents) ? allAgents : [];
    return new Map(agents.map((a: { id: string; name: string }) => [a.id, a.name]));
  }, [allAgents]);

  // Agents in this hive (from graph nodes)
  const hiveAgents = useMemo(() => graph?.nodes ?? [], [graph]);

  // Agents not yet in the hive
  const availableAgents = useMemo(() => {
    const hiveAgentIds = new Set(hiveAgents.map((n) => n.id));
    const agents = Array.isArray(allAgents) ? allAgents : [];
    return agents.filter((a: { id: string }) => !hiveAgentIds.has(a.id));
  }, [allAgents, hiveAgents]);

  // ---------------------------------------------------------------------------
  // Loading / Error
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !hive) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">
            {t('common.error')}: {error instanceof Error ? error.message : 'Hive not found'}
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleDelete = async () => {
    if (!window.confirm(t('admin.hives.confirmDelete'))) return;
    try {
      await deleteMutation.mutateAsync(hive.id);
      toast.success(t('admin.hives.deleteSuccess') || 'Hive deleted');
      navigate('/agents/hives');
    } catch {
      toast.error(t('admin.hives.deleteError') || 'Failed to delete hive');
    }
  };

  const handleAddAgent = async (agentId: string) => {
    try {
      await addAgentMutation.mutateAsync({ agent_id: agentId });
      toast.success(t('admin.hives.agentAdded') || 'Agent added to hive');
    } catch {
      toast.error(t('admin.hives.agentAddError') || 'Failed to add agent');
    }
  };

  const handleSetEntry = async (agentId: string) => {
    try {
      await addAgentMutation.mutateAsync({ agent_id: agentId, is_entry: true });
      toast.success(t('admin.hives.entrySet') || 'Entry agent updated');
    } catch {
      toast.error(t('admin.hives.entrySetError') || 'Failed to update entry agent');
    }
  };

  const handleRemoveAgent = async (agentId: string) => {
    try {
      await removeAgentMutation.mutateAsync(agentId);
      toast.success(t('admin.hives.agentRemoved') || 'Agent removed from hive');
    } catch {
      toast.error(t('admin.hives.agentRemoveError') || 'Failed to remove agent');
    }
  };

  const handleDeleteHandoff = async (handoffId: string) => {
    try {
      await deleteHandoffMutation.mutateAsync(handoffId);
      toast.success(t('admin.hives.handoffDeleted') || 'Handoff deleted');
    } catch {
      toast.error(t('admin.hives.handoffDeleteError') || 'Failed to delete handoff');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6" data-testid="hive-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/agents/hives')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <Network className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {hive.name}
              </h1>
              {hive.description && (
                <p className="text-gray-500 dark:text-gray-400">
                  {stripImportedField(hive.description)}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* ISS-412: use_only no se exporta a CAC (caja negra de plataforma) */}
          {!isUseOnly && (
            <Button variant="outline" onClick={() => navigate('/marketplace/publish')}>
              <Package className="h-4 w-4 mr-1" />
              Export to CAC
            </Button>
          )}
          {/* ISS-409: la definición de un hive use_only no se elimina desde el tenant */}
          {!isUseOnly && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              {t('common.delete')}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabTrigger value="overview">{t('admin.hives.tabs.overview')}</TabTrigger>
          <TabTrigger value="agents">{t('admin.hives.tabs.agents')}</TabTrigger>
          <TabTrigger value="handoffs">{t('admin.hives.tabs.handoffs')}</TabTrigger>
          <TabTrigger value="graph">{t('admin.hives.tabs.graph')}</TabTrigger>
          <TabTrigger value="skills">Skills</TabTrigger>
          <TabTrigger value="capabilities">{t('admin.hives.tabs.capabilities') || 'Capabilities'}</TabTrigger>
        </TabsList>

        {/* ─────────────── Overview Tab ─────────────── */}
        <TabContent value="overview">
          <OverviewTab hive={hive} updateMutation={updateMutation} readOnly={isUseOnly} t={t} />
        </TabContent>

        {/* ─────────────── Agents Tab ─────────────── */}
        <TabContent value="agents">
          <AgentsTab
            hiveAgents={hiveAgents}
            availableAgents={availableAgents}
            agentMap={agentMap}
            entryAgentId={hive.entry_agent_id}
            onAdd={handleAddAgent}
            onRemove={handleRemoveAgent}
            onSetEntry={handleSetEntry}
            readOnly={isUseOnly}
            t={t}
          />
        </TabContent>

        {/* ─────────────── Handoffs Tab ─────────────── */}
        <TabContent value="handoffs">
          <HandoffsTab
            hiveId={hive.id}
            handoffs={handoffs}
            agentMap={agentMap}
            onDelete={handleDeleteHandoff}
            readOnly={isUseOnly}
            t={t}
          />
        </TabContent>

        {/* ─────────────── Graph Tab ─────────────── */}
        <TabContent value="graph">
          <Card className="p-6 mt-4">
            {graph?.nodes && graph.nodes.length > 0 ? (
              <HiveGraphView nodes={graph.nodes} edges={graph.edges ?? []} />
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('admin.hives.graph.empty')}</p>
              </div>
            )}
          </Card>
        </TabContent>

        {/* ─────────────── Skills Tab (Sprint 133 Stream C) ─────────────── */}
        <TabContent value="skills">
          <SkillsTab
            hiveId={hive.id}
            members={hiveAgents.map((n) => ({
              id: n.id,
              name: agentMap.get(n.id) ?? n.id,
            }))}
          />
        </TabContent>

        {/* ─────────────── Capabilities Tab (Sprint 267 — REQ-CAP-002 §4) ─────────────── */}
        <TabContent value="capabilities">
          <HiveCapabilitiesTab hiveId={hive.id} />
        </TabContent>
      </Tabs>
    </div>
  );
}

// =============================================================================
// Overview Tab — Edit name, description, settings
// =============================================================================

function OverviewTab({
  hive,
  updateMutation,
  readOnly = false,
  t,
}: {
  hive: Hive;
  updateMutation: ReturnType<typeof useUpdateHive>;
  readOnly?: boolean;
  t: (key: string) => string;
}) {
  // ISS-409: en entidades use_only solo los parámetros de USO son editables
  // (router habilitado + runner allow-list). Nombre/descripción/router_prompt
  // son definición → solo lectura.
  const [name, setName] = useState(hive.name);
  const [description, setDescription] = useState(stripImportedField(hive.description ?? ''));
  const [routerEnabled, setRouterEnabled] = useState(hive.router_enabled);
  const [routerPrompt, setRouterPrompt] = useState(hive.router_prompt ?? '');
  const [runnerAllowlist, setRunnerAllowlist] = useState(
    ((hive.settings as { runner_policy?: { allowed_runner_ids?: string[] } } | undefined)
      ?.runner_policy
      ?.allowed_runner_ids ?? [])
      .join(', ')
  );
  // Sprint 250 (DEBT-13): hive-level delegation defaults (settings.delegation).
  const hiveDelegation = (
    hive.settings as
      | { delegation?: { max_depth?: number; max_fan_out?: number; delegation_prompt?: string } }
      | undefined
  )?.delegation;
  const [hiveMaxDepth, setHiveMaxDepth] = useState<number | ''>(
    hiveDelegation?.max_depth ?? ''
  );
  const [hiveMaxFanout, setHiveMaxFanout] = useState<number | ''>(
    hiveDelegation?.max_fan_out ?? ''
  );
  const [hiveDelegationPrompt, setHiveDelegationPrompt] = useState(
    hiveDelegation?.delegation_prompt ?? ''
  );
  // Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1): overrides de la política
  // del pipeline (settings.routing/optimization/loop/autonomy/timeouts).
  const [pipelineSettings, setPipelineSettings] = useState<Record<string, unknown>>(
    { ...((hive.settings as Record<string, unknown>) ?? {}) }
  );
  // REQ-AGT-008 (Sprint 281 Stream C): plantillas de plan del hive (JSON).
  // La validación canónica es fail-closed en el servidor; aquí solo se
  // comprueba el JSON para dar feedback inmediato.
  const initialPlanTemplatesJson = JSON.stringify(
    ((hive.settings as { plan_templates?: unknown } | undefined)?.plan_templates ?? []),
    null,
    2
  );
  const [planTemplatesJson, setPlanTemplatesJson] = useState(initialPlanTemplatesJson);
  const [planTemplatesError, setPlanTemplatesError] = useState<string | null>(null);

  const initialRunnerAllowlist = (
    ((hive.settings as { runner_policy?: { allowed_runner_ids?: string[] } } | undefined)
      ?.runner_policy
      ?.allowed_runner_ids ?? [])
  )
    .join(', ');

  const initialMaxDepth = hiveDelegation?.max_depth ?? '';
  const initialMaxFanout = hiveDelegation?.max_fan_out ?? '';
  const initialDelegationPrompt = hiveDelegation?.delegation_prompt ?? '';

  // ISS-409: en use_only el dirty solo refleja cambios de parámetros de uso
  // (router habilitado + runner allow-list). router_prompt es definición.
  const pipelineChanged =
    JSON.stringify(pipelineSettings) !== JSON.stringify(hive.settings ?? {});
  const isDirty = readOnly
    ? routerEnabled !== hive.router_enabled || runnerAllowlist !== initialRunnerAllowlist
    : name !== hive.name ||
      description !== stripImportedField(hive.description ?? '') ||
      routerEnabled !== hive.router_enabled ||
      routerPrompt !== (hive.router_prompt ?? '') ||
      runnerAllowlist !== initialRunnerAllowlist ||
      hiveMaxDepth !== initialMaxDepth ||
      hiveMaxFanout !== initialMaxFanout ||
      hiveDelegationPrompt !== initialDelegationPrompt ||
      planTemplatesJson !== initialPlanTemplatesJson ||
      pipelineChanged;

  const handleSave = async () => {
    try {
      const update: HiveUpdate = {};
      if (readOnly) {
        // Solo parámetros de uso (backend proyecta si aplica).
        if (routerEnabled !== hive.router_enabled) update.router_enabled = routerEnabled;
        if (runnerAllowlist !== initialRunnerAllowlist) {
          update.settings = {
            ...(hive.settings ?? {}),
            runner_policy: {
              allowed_runner_ids: runnerAllowlist
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean),
            },
          };
        }
      } else {
        if (name !== hive.name) update.name = name;
        if (description !== stripImportedField(hive.description ?? '')) update.description = description;
        if (routerEnabled !== hive.router_enabled) update.router_enabled = routerEnabled;
        if (routerPrompt !== (hive.router_prompt ?? '')) update.router_prompt = routerPrompt;

        const runnerChanged = runnerAllowlist !== initialRunnerAllowlist;
        const delegationChanged =
          hiveMaxDepth !== initialMaxDepth ||
          hiveMaxFanout !== initialMaxFanout ||
          hiveDelegationPrompt !== initialDelegationPrompt;
        const templatesChanged = planTemplatesJson !== initialPlanTemplatesJson;
        let planTemplatesParsed: unknown[] | null = null;
        if (templatesChanged) {
          try {
            const parsed = JSON.parse(planTemplatesJson);
            if (!Array.isArray(parsed)) {
              throw new Error('not an array');
            }
            planTemplatesParsed = parsed;
            setPlanTemplatesError(null);
          } catch {
            setPlanTemplatesError(
              'JSON inválido: se espera un array de plantillas (id tpl_*, version, trigger, steps)'
            );
            return;
          }
        }

        if (runnerChanged || delegationChanged || pipelineChanged || templatesChanged) {
          const allowedRunnerIds = runnerAllowlist
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
          const settingsUpdate: Record<string, unknown> = {
            ...(hive.settings ?? {}),
          };
          if (runnerChanged) {
            settingsUpdate.runner_policy = {
              allowed_runner_ids: allowedRunnerIds,
            };
          }
          if (delegationChanged) {
            settingsUpdate.delegation = {
              ...(hiveDelegation ?? {}),
              ...(hiveMaxDepth !== '' ? { max_depth: hiveMaxDepth } : {}),
              ...(hiveMaxFanout !== '' ? { max_fan_out: hiveMaxFanout } : {}),
              ...(hiveDelegationPrompt.trim()
                ? { delegation_prompt: hiveDelegationPrompt.trim() }
                : {}),
            };
          }
          if (pipelineChanged) {
            // Sprint 278 §8.1: bloques gestionados por el editor de política
            // (retirar el bloque = volver a heredar).
            for (const block of [
              'routing',
              'optimization',
              'loop',
              'autonomy',
              'timeouts',
            ]) {
              if (block in pipelineSettings) {
                settingsUpdate[block] = pipelineSettings[block];
              } else {
                delete settingsUpdate[block];
              }
            }
          }
          if (templatesChanged && planTemplatesParsed) {
            // REQ-AGT-008: array vacío ⇒ se retira el bloque (heredar/sin
            // plantillas); el servidor valida el resto fail-closed.
            if (planTemplatesParsed.length) {
              settingsUpdate.plan_templates = planTemplatesParsed;
            } else {
              delete settingsUpdate.plan_templates;
            }
          }
          update.settings = settingsUpdate;
        }
      }
      await updateMutation.mutateAsync(update);
      toast.success(t('admin.hives.updateSuccess') || 'Hive updated');
    } catch {
      toast.error(t('admin.hives.updateError') || 'Failed to update hive');
    }
  };

  return (
    <Card className="p-6 mt-4 space-y-6 max-w-2xl">
      <div>
        <Label htmlFor="hive-name">{t('admin.hives.form.name')}</Label>
        <Input
          id="hive-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readOnly}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="hive-description">{t('admin.hives.form.description')}</Label>
        <Textarea
          id="hive-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={readOnly}
          className="mt-1"
          rows={3}
        />
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="router-enabled"
          checked={routerEnabled}
          onChange={(e) => setRouterEnabled(e.target.checked)}
          className="h-4 w-4 text-purple-600 rounded"
        />
        <Label htmlFor="router-enabled">{t('admin.hives.form.routerEnabled')}</Label>
      </div>

      {routerEnabled && (
        <div>
          <Label htmlFor="router-prompt">{t('admin.hives.form.routerPrompt')}</Label>
          <Textarea
            id="router-prompt"
            value={routerPrompt}
            onChange={(e) => setRouterPrompt(e.target.value)}
            disabled={readOnly}
            className="mt-1"
            rows={5}
            placeholder={t('admin.hives.form.routerPromptPlaceholder')}
          />
          {readOnly && (
            <p className="text-xs text-muted-foreground mt-1">
              Definido por la plataforma en entidades use_only (solo lectura).
            </p>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('admin.hives.form.delegationTitle')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hive-max-depth">{t('admin.hives.form.maxDepth')}</Label>
              <Input
                id="hive-max-depth"
                type="number"
                value={hiveMaxDepth}
                onChange={(e) =>
                  setHiveMaxDepth(e.target.value === '' ? '' : Number(e.target.value))
                }
                min={1}
                max={5}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="hive-max-fanout">{t('admin.hives.form.maxFanout')}</Label>
              <Input
                id="hive-max-fanout"
                type="number"
                value={hiveMaxFanout}
                onChange={(e) =>
                  setHiveMaxFanout(e.target.value === '' ? '' : Number(e.target.value))
                }
                min={1}
                max={10}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="hive-delegation-prompt">{t('admin.hives.form.delegationPrompt')}</Label>
            <Textarea
              id="hive-delegation-prompt"
              value={hiveDelegationPrompt}
              onChange={(e) => setHiveDelegationPrompt(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Pipeline policy (Routing · Optimización · Loops/Avanzado)
          </h3>
          <p className="text-xs text-muted-foreground">
            Overrides del hive para la política del pipeline.
            Los campos en «Inherit» se resuelven con la política del
            tenant/DR; el override del agente gana sobre el del hive.
          </p>
          <PipelinePolicyOverrides
            settings={pipelineSettings}
            onChange={(next) => setPipelineSettings(next)}
          />
        </div>
      )}

      {!readOnly && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Plantillas de plan
          </h3>
          <p className="text-xs text-muted-foreground">
            Flujos SOP deterministas del hive (JSON). Validación fail-closed al
            guardar: id <code>tpl_*</code>, versión ≥ 1, trigger con ejemplos y
            umbral, pasos con el contrato del plan DAG (ejecutor, modo,
            depends_on, input_refs) y condiciones <code>when</code>{' '}
            deterministas. Un array vacío retira las plantillas.
          </p>
          <Textarea
            id="hive-plan-templates"
            value={planTemplatesJson}
            onChange={(e) => setPlanTemplatesJson(e.target.value)}
            className="mt-1 font-mono text-xs"
            rows={10}
            spellCheck={false}
            data-testid="hive-plan-templates"
          />
          {planTemplatesError && (
            <p
              className="text-xs text-red-600 dark:text-red-400"
              data-testid="hive-plan-templates-error"
            >
              {planTemplatesError}
            </p>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="hive-runner-allowlist">Runner Allowlist (IDs)</Label>
        <Textarea
          id="hive-runner-allowlist"
          value={runnerAllowlist}
          onChange={(e) => setRunnerAllowlist(e.target.value)}
          className="mt-1"
          rows={3}
          placeholder="agent_01ABC..., agent_01XYZ..."
        />
        <p className="text-xs text-muted-foreground mt-1">
          If empty, hive does not restrict runners. If set, only these runners are allowed by hive policy.
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
          {updateMutation.isPending ? <Spinner size="sm" /> : t('common.save')}
        </Button>
      </div>
    </Card>
  );
}

// =============================================================================
// Agents Tab — List agents in hive, add/remove
// =============================================================================

function AgentsTab({
  hiveAgents,
  availableAgents,
  agentMap: _agentMap,
  entryAgentId: _entryAgentId,
  onAdd,
  onRemove,
  onSetEntry,
  readOnly = false,
  t,
}: {
  hiveAgents: GraphNode[];
  availableAgents: { id: string; name: string }[];
  agentMap: Map<string, string>;
  entryAgentId?: string;
  onAdd: (agentId: string) => void;
  onRemove: (agentId: string) => void;
  onSetEntry?: (agentId: string) => void;
  readOnly?: boolean;
  t: (key: string) => string;
}) {
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div className="mt-4 space-y-4">
      {/* Add agent section — oculto en use_only (plataforma gestiona los miembros) */}
      {!readOnly && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setShowAdd(!showAdd)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.hives.addAgent')}
          </Button>
        </div>
      )}

      {showAdd && availableAgents.length > 0 && (
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {t('admin.hives.selectAgent')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {availableAgents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => {
                  onAdd(agent.id);
                  setShowAdd(false);
                }}
                className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
              >
                <Bot className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {agent.name}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Agents table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.hives.table.agent')}</TableHead>
            <TableHead>{t('admin.hives.table.role')}</TableHead>
            <TableHead>{t('admin.hives.table.handoffs')}</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {hiveAgents.length === 0 ? (
            <TableEmpty colSpan={4} message={t('admin.hives.noAgents')} />
          ) : (
            hiveAgents.map((node) => (
              <TableRow key={node.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {node.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {node.is_entry ? (
                    <StatusBadge status="active" label={t('admin.hives.entryAgent')} />
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('admin.hives.member')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-gray-600 dark:text-gray-400">
                  {node.handoff_count}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {!node.is_entry && !readOnly && onSetEntry && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSetEntry(node.id)}
                        data-testid={`set-entry-${node.id}`}
                      >
                        {t('admin.hives.setEntry')}
                      </Button>
                    )}
                    {!node.is_entry && !readOnly && (
                      <IconButton
                        icon="trash"
                        label={t('common.remove')}
                        onClick={() => onRemove(node.id)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// Handoffs Tab — List handoffs with delete
// =============================================================================

function HandoffsTab({
  hiveId,
  handoffs,
  agentMap,
  onDelete,
  readOnly = false,
  t,
}: {
  hiveId: string;
  handoffs: Handoff[];
  agentMap: Map<string, string>;
  onDelete: (handoffId: string) => void;
  readOnly?: boolean;
  t: (key: string) => string;
}) {
  const navigate = useNavigate();

  const mapHandoffType = (type: string): StatusType => {
    if (type === 'transfer') return 'active';
    return 'default';
  };

  return (
    <div className="mt-4 space-y-4">
      {/* ISS-409/411: en use_only la topología es legible pero no editable
          (los handoffs los define la plataforma). */}
      {!readOnly && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => navigate(`/agents/hives/${hiveId}/handoffs/new`)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.hives.newHandoff')}
          </Button>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.hives.table.source')}</TableHead>
            <TableHead></TableHead>
            <TableHead>{t('admin.hives.table.target')}</TableHead>
            <TableHead>{t('admin.hives.table.type')}</TableHead>
            <TableHead>{t('admin.hives.table.priority')}</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {handoffs.length === 0 ? (
            <TableEmpty colSpan={6} message={t('admin.hives.noHandoffs')} />
          ) : (
            handoffs.map((ho) => (
              <TableRow key={ho.id}>
                <TableCell>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {agentMap.get(ho.source_agent_id) ?? ho.source_agent_id.slice(0, 12)}
                  </span>
                </TableCell>
                <TableCell>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </TableCell>
                <TableCell>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {ho.target_agent_name ?? agentMap.get(ho.target_agent_id) ?? ho.target_agent_id.slice(0, 12)}
                  </span>
                </TableCell>
                <TableCell>
                  <StatusBadge status={mapHandoffType(ho.type)} label={ho.type} />
                </TableCell>
                <TableCell className="text-gray-600 dark:text-gray-400">
                  {ho.priority}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {!readOnly && (
                      <IconButton
                        icon="edit"
                        label={t('common.edit')}
                        onClick={() =>
                          navigate(`/agents/hives/${hiveId}/handoffs/${ho.id}/edit`)
                        }
                      />
                    )}
                    {!readOnly && (
                      <IconButton
                        icon="trash"
                        label={t('common.delete')}
                        onClick={() => onDelete(ho.id)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// Skills Tab (Sprint 133 Stream C)
// =============================================================================

interface SkillsTabProps {
  hiveId: string;
  members: { id: string; name: string }[];
}

function SkillsTab({ hiveId, members }: SkillsTabProps) {
  const { data: bindings = [], isLoading } = useHiveSkillBindings(hiveId);
  const removeBinding = useDeleteHiveSkillBinding(hiveId);
  const [addOpen, setAddOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const boundIds = useMemo(
    () => new Set(bindings.map((b) => b.skill_id)),
    [bindings],
  );

  async function handleRemove(skillId: string) {
    if (!confirm('Remove this skill from the hive?')) return;
    try {
      await removeBinding.mutateAsync(skillId);
      toast.success('Skill removed from hive');
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Failed to remove skill');
    }
  }

  return (
    <Card className="p-6 mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Hive skills
          </h3>
          <p className="text-sm text-muted-foreground">
            Skills attached here apply to every member agent. Agent-level
            bindings on the same skill override these defaults.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            disabled={members.length === 0}
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview inheritance
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Attach skill
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Skill</TableHead>
              <TableHead>Categorías</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {bindings.length === 0 ? (
              <TableEmpty colSpan={6} message="No skills attached to this hive yet." />
            ) : (
              bindings.map((b) => (
                <TableRow key={b.skill_id}>
                  {/* ISS-410: mostrar el nombre de la skill; el UUID nunca como label */}
                  <TableCell>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {b.skill_name ?? b.skill_id}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(b.categories ?? []).length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        (b.categories ?? []).map((cat) => (
                          <span
                            key={cat}
                            className="inline-flex items-center rounded-full bg-purple-50 dark:bg-purple-900/40 px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300"
                          >
                            {cat}
                          </span>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{b.mode}</TableCell>
                  <TableCell className="text-xs">
                    {b.priority}
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={(b.enabled ? 'success' : 'inactive') as StatusType}
                    >
                      {b.enabled ? 'enabled' : 'disabled'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      icon="trash"
                      label="Remove from hive"
                      onClick={() => handleRemove(b.skill_id)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <HiveSkillAddModal
        open={addOpen}
        onOpenChange={setAddOpen}
        hiveId={hiveId}
        alreadyBoundSkillIds={boundIds}
      />
      <HiveSkillInheritanceModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        hiveId={hiveId}
        members={members}
      />
    </Card>
  );
}
